"use client";
import React, { FC } from "react";
import IconTextListItem from "@/components/TextLists/IconTextList/IconTextListItem";
import { getCourseInformation } from "@/lib/course/utils/getCourseInformation";
import { ProcessedCourse } from "@/common/course/types";
import { Skeleton } from "@/components/ui/skeleton";
import IconTextGrid from "@/components/TextLists/IconTextList/IconTextGrid";

export type Props = {
  course: ProcessedCourse;
  paymentOption: string | null;
  className?: string;
  hasDateColumn?: boolean;
  isLoading?: boolean;
  isArchivedExternally?: boolean;
  certificateFactoid?: {
    showFactoidDesktop: boolean;
    showFactoidMobile: boolean;
    factoidText: string;
    factoidHeader: string;
    position: number;
  };
};

const Loader: FC = () =>
  Array.from({ length: 3 }).map((_, i) => (
    <div key={i} className="flex items-center space-x-4 mr-36">
      <Skeleton className="h-12 w-12 rounded-full" />
      <div className="space-y-2">
        <Skeleton className="h-2 w-[150px]" />
        <Skeleton className="h-2 w-[200px]" />
      </div>
    </div>
  ));

export default function CourseSnapshot({
  course,
  paymentOption,
  className,
  hasDateColumn,
  isArchivedExternally = false,
  certificateFactoid,
}: Props) {
  const courseInformation = getCourseInformation(
    course,
    paymentOption,
    hasDateColumn,
    isArchivedExternally,
  );

  if (certificateFactoid) {
    const factoidItem = {
      icon: "certificate",
      header: certificateFactoid.factoidHeader,
      description: certificateFactoid.factoidText,
    };

    courseInformation.splice(certificateFactoid.position, 0, factoidItem);
  }

  const certificateFactoidClassName = `${certificateFactoid?.showFactoidMobile ? "block" : "hidden"} ${certificateFactoid?.showFactoidDesktop ? "md:block" : "md:hidden"}`;

  const IconTextListItems: FC = () =>
    courseInformation.map((item, key) => {
      const k = key >= 2 && hasDateColumn ? key - 1 : key;
      const descriptionOverride =
        item.header === "Instructor-paced" ? "Scheduled classes" : null;
      return (
        <IconTextListItem
          key={key}
          icon={item.icon}
          header={item.header}
          boldHeader={true}
          description={descriptionOverride ?? item.description}
          small
          className={`${item.icon === "certificate" ? certificateFactoidClassName : ""}`}
        />
      );
    });

  return (
    <div className="flex w-full mt-4">
      <IconTextGrid fullwidth={false} noWrap={false} small>
        <IconTextListItems />
      </IconTextGrid>
    </div>
  );
}
